/**
 * Pure helpers backing the Usage / Billing dashboard and the public Status
 * page.
 *
 * Kept framework-free so they can be unit-tested in the app's node test env
 * (mirrors `utilities/ops-utilities.ts` and `utilities/services-utilities.ts`).
 * These cover: mapping a usage figure to a percent-of-limit, formatting tier
 * limits for display (including the sentinel "unlimited" values the API uses),
 * deriving the "approaching / over limit" state of a metered dimension, and
 * deriving overall + per-component status from a `/health` response.
 *
 * No HTTP, no React. The components consume these and stay thin.
 *
 * @module
 */

// ---------------------------------------------------------------------------
// Usage rollup shape — mirrors GET /account/usage and GET /projects/:id/usage
// (api/src/App/Resource/Usage/{service,index}.ts). Frontend-local copy so the
// app doesn't import from the API package.
// ---------------------------------------------------------------------------

/** The billing period window the figures cover. */
export interface BillingPeriod {
  /** Inclusive ISO start of the period. */
  start: string
  /** Exclusive ISO end of the period. */
  end: string
}

/** A single (provider, category) brokered-usage line in a rollup. */
export interface BrokerUsageLine {
  /** Registry provider key, e.g. `anthropic`. */
  provider: string
  /** Brokerability class, e.g. `ai`. */
  category: string
  /** Total forwarded requests in the period. */
  requests: number
  /** Total normalized units (e.g. tokens). */
  units: number
  /**
   * Optional billed/at-cost figure for this line in cents. Present on some
   * rollups (e.g. usage-billing accounts); when absent the line is summarized
   * by requests/units only.
   */
  costCents?: number
  /**
   * The marked-up amount the platform bills for this line, in cents — the field the usage
   * rollup actually sends (R141-M1). Preferred over {@link costCents}.
   */
  billedCents?: number
}

/**
 * The effective tier limit set returned alongside the rollup. Only the fields
 * the dashboard renders are typed here; the API's `TierLimits` carries more.
 */
export interface UsageLimits {
  maxProjects: number
  maxAiMessagesPerDay: number
  maxStorageMb: number
  maxActiveDeployments: number
  maxCustomDomains: number
  /**
   * The tier's Synthase build allowance (api tiers.ts `AiBudget`): `cents` of
   * upstream AI spend per `window`. The WINDOW varies by tier — unpaid tiers
   * meter over a rolling day, paid tiers over their billing period — so never
   * label this "daily" without reading it. Optional: an API predating the field
   * sends nothing, and callers then have no allowance to compare against.
   */
  aiBudget?: { cents: number; window: 'daily' | 'billing-period' }
}

/** Full usage response from the account / project usage endpoints. */
export interface UsageResponse {
  period: BillingPeriod
  broker: BrokerUsageLine[]
  deployments: number
  customDomains: number
  storageMb: number
  /** AI spend so far in the CURRENT allowance window, in cents (vs `limits.aiBudget.cents`). */
  aiCostCentsToday: number
  /**
   * The user's DURABLE, lifetime "overall AI cost" in US cents — the total
   * Synthase IDE-chat AI spend persisted per-turn server-side (the `aiUsage`
   * ledger; `sumAiUsageCents`), summed across every model and project and
   * surviving project deletion. This is the single authoritative answer to
   * "how much AI has this user cost overall" (P2-05).
   *
   * Deliberately DISTINCT from two neighbours and never to be conflated with
   * them: {@link aiCostCentsToday} is only a transient allowance window (a
   * PER-USER ROLLING 24h span anchored at the user's first chargeable message —
   * NOT a shared UTC midnight; `aiMeter.resetsAt` carries its exact end), and
   * the {@link broker} `costCents` lines cover only brokered-app traffic, never
   * the IDE's own chat.
   *
   * Present ONLY on `GET /account/usage` (account-wide); `GET /projects/:id/usage`
   * and older API builds omit it — hence optional. Use {@link hasOverallAiCost}
   * to decide whether to render it (so an absent figure isn't shown as `$0.00`).
   */
  aiUsageCentsTotal?: number
  /**
   * The same durable Synthase IDE-chat AI spend as {@link aiUsageCentsTotal} but
   * scoped to the current billing {@link period} (month-to-date), so a dashboard
   * can show "this month" alongside the lifetime total. Same window as the
   * brokered `period` figures. Present only on `GET /account/usage`.
   */
  aiUsageCentsThisPeriod?: number
  /**
   * What the user should DO about their AI usage right now, decided server-side
   * ({@link BillingActionKind} in `upgrade-copy.ts`) — `'none'` when nothing is
   * blocking them.
   *
   * It exists because "the allowance ran out" has several different fixes that
   * the client cannot tell apart from the meter alone: add funds, add a card,
   * raise your own spend cap, opt into extra usage, or (for a teammate) ask the
   * project owner. Optional: an API predating the field sends nothing, and the
   * surfaces fall back to deriving from the metric.
   */
  billingAction?: string
  /** Effective plan key (null = free). */
  plan: string | null
  /**
   * The plan's TIER CATEGORY ('anonymous' | 'free' | 'pro' | 'team'), for copy
   * that names the tier. Server-derived because {@link plan} is a raw key
   * (usage-pack and seat keys included) that the client must not try to collapse
   * itself. Optional: an API predating the field sends nothing.
   */
  planCategory?: string
  /**
   * Display name for that plan, pack included (e.g. `Pro · Extended AI`).
   * Server-derived so the pack names have one home. Optional: an older API
   * does not send it, and the client falls back to labelling the base tier.
   */
  planName?: string
  limits: UsageLimits
  /**
   * The user-facing AI usage meter — a UNITLESS 0–100% of the allowance for
   * the current window. This is the figure every UI surface renders for AI
   * usage; the cents fields above are internal/ops detail and must not be shown
   * to users as currency.
   *
   * `window` is NOT decoration: the unpaid tiers meter over a rolling day, the
   * paid ones over their billing period (monthly, a yearly subscriber
   * included). Every label that names a span must read it — hardcoding "daily"
   * is what told Pro users their monthly allowance was "today's".
   */
  aiMeter?: {
    usedPercent: number
    window: 'daily' | 'billing-period'
    resetsAt: string | null
    exhausted: boolean
    unmetered: boolean
    /**
     * Paid + opted-in only: the extra-usage meter. `fundsAvailable` is the
     * server's own admission input (billable AND a positive prepaid balance);
     * optional because an older API omits it.
     */
    payg?: { active: boolean; usedPercent: number; fundsAvailable?: boolean }
  }
}

/**
 * The sentinel value the API tiers use for an effectively-unlimited limit
 * (Team tier). Anything `>=` this is treated as unlimited for display and is
 * never rendered as a bounded progress bar.
 */
export const UNLIMITED_THRESHOLD = 999999

// ---------------------------------------------------------------------------
// Limit / percent math
// ---------------------------------------------------------------------------

/**
 * Whether a limit value represents "unlimited" — either the API sentinel
 * (`>= UNLIMITED_THRESHOLD`) or a non-finite value (`Infinity`, `NaN`-guarded).
 * A negative limit is also treated as unlimited (no meaningful cap).
 *
 * @param limit - The raw tier limit.
 * @returns True when the limit should not be rendered as a bounded bar.
 */
export function isUnlimited(limit: number): boolean {
  if (!Number.isFinite(limit)) return true
  return limit >= UNLIMITED_THRESHOLD || limit < 0
}

/**
 * Map a usage figure to a percent (0–100) of its limit, clamped. Unlimited
 * limits always return 0 (the bar renders as effectively empty / "no cap").
 * A zero limit with non-zero usage is "over" → 100; zero usage of a zero
 * limit is 0.
 *
 * @param value - The measured usage.
 * @param limit - The tier limit for this dimension.
 * @returns A clamped percent in `[0, 100]`.
 */
export function usagePercent(value: number, limit: number): number {
  if (isUnlimited(limit)) return 0
  const v = Number.isFinite(value) && value > 0 ? value : 0
  if (limit <= 0) return v > 0 ? 100 : 0
  return Math.min(Math.round((v / limit) * 100), 100)
}

/**
 * Format a tier limit for display. Unlimited limits render as the em-dash-free
 * word "Unlimited"; finite limits render as the integer string. An optional
 * `unit` suffix (e.g. `'MB'`) is appended to finite values only.
 *
 * @param limit - The raw tier limit.
 * @param unit - Optional unit suffix appended to finite values.
 * @returns A display string.
 */
export function formatLimit(limit: number, unit?: string): string {
  if (isUnlimited(limit)) return 'Unlimited'
  const n = Number.isFinite(limit) ? Math.round(limit) : 0
  return unit ? `${n} ${unit}` : String(n)
}

/**
 * Format a storage figure in MB as a human string, promoting to GB at/above
 * 1024 MB. Whole GB render without a trailing `.0`; fractional GB keep one
 * decimal.
 *
 * @param mb - The storage figure in megabytes.
 * @returns A display string like `"512 MB"` or `"1.5 GB"`.
 */
export function formatStorage(mb: number): string {
  const m = Number.isFinite(mb) && mb > 0 ? mb : 0
  if (m < 1024) return `${Math.round(m)} MB`
  const gb = m / 1024
  return Number.isInteger(gb) ? `${gb} GB` : `${gb.toFixed(1)} GB`
}

/**
 * Format a "used of limit" label, handling unlimited limits gracefully.
 *
 * @param value - The measured usage.
 * @param limit - The tier limit.
 * @param unit - Optional unit suffix.
 * @returns e.g. `"3 / 10"`, `"3 / Unlimited"`, or with a unit `"512 / 5120 MB"`.
 */
export function formatUsageLabel(value: number, limit: number, unit?: string): string {
  const v = Number.isFinite(value) && value > 0 ? Math.round(value) : 0
  const left = unit ? `${v} ${unit}` : String(v)
  if (isUnlimited(limit)) return `${left} / ${'Unlimited'}`
  return `${left} / ${formatLimit(limit, unit)}`
}

// ---------------------------------------------------------------------------
// Limit state — drives the bar color + any warning copy
// ---------------------------------------------------------------------------

/** The state of a metered dimension relative to its limit. */
export type LimitState = 'ok' | 'approaching' | 'over' | 'unlimited'

/** Default fraction (0–1) at which usage is considered "approaching" the cap. */
export const APPROACHING_FRACTION = 0.8

/**
 * Derive the {@link LimitState} for a usage figure against its limit.
 * Unlimited limits are always `'unlimited'`. Otherwise: `'over'` when usage
 * meets or exceeds the limit, `'approaching'` at/above `approachingFraction`
 * of the limit, else `'ok'`. A zero (or negative) finite limit with any usage
 * is `'over'`.
 *
 * @param value - The measured usage.
 * @param limit - The tier limit.
 * @param approachingFraction - The fraction at which to warn (defaults to
 *   {@link APPROACHING_FRACTION}).
 * @returns The dimension's limit state.
 */
export function limitState(
  value: number,
  limit: number,
  approachingFraction: number = APPROACHING_FRACTION,
): LimitState {
  if (isUnlimited(limit)) return 'unlimited'
  const v = Number.isFinite(value) && value > 0 ? value : 0
  if (limit <= 0) return v > 0 ? 'over' : 'ok'
  if (v >= limit) return 'over'
  if (v / limit >= approachingFraction) return 'approaching'
  return 'ok'
}

/** Theme-friendly color for each limit state (CSS-var-anchored, readable in both themes). */
export const LIMIT_STATE_COLOR: Record<LimitState, string> = {
  ok: 'var(--color-primary-light, #4070e0)',
  // Used as a bar-fill/accent (Billing usage bar, UsageInspector) — the readable warning
  // token, not the old hardcoded #eab308 that was unreadable in light theme (P5-10 sweep).
  approaching: 'var(--color-warning, #988828)',
  over: 'var(--mol-color-error, #ef4444)',
  unlimited: 'var(--color-primary-light, #4070e0)',
}

// ---------------------------------------------------------------------------
// Nudge roll-up — drives the proactive "approaching limit" upgrade banner
// ---------------------------------------------------------------------------

/**
 * A single metered metric resolved against its tier limit, used by the
 * proactive {@link UsageNudge} banner. `key` is a stable identifier (also the
 * i18n metric-label key suffix), `used`/`limit` are the raw figures, and
 * `state` is the derived {@link LimitState}.
 */
export interface NudgeMetric {
  /** Stable metric identifier, e.g. `'deployments'`, `'storageMb'`. */
  key: string
  /** Whether this metric is a storage figure (renders with the GB formatter). */
  storage?: boolean
  /** Whether this metric is a money figure in cents (renders as `$`). */
  cost?: boolean
  /** The measured usage. */
  used: number
  /** The effective tier limit. */
  limit: number
  /** The derived limit state. */
  state: LimitState
}

/** The overall severity of an account's usage relative to its limits. */
export type NudgeSeverity = 'none' | 'approaching' | 'over'

/** The result of rolling a usage response up to a single nudge decision. */
export interface NudgeRollup {
  /** Worst state across all bounded metrics (`'over'` > `'approaching'` > `'none'`). */
  severity: NudgeSeverity
  /** Every bounded metric present in the response, with its resolved state. */
  metrics: NudgeMetric[]
}

/**
 * Declarative mapping of a top-level usage figure to its tier-limit field.
 * Iterating this (rather than hard-coding three `limitState(...)` calls) means
 * adding a new metered dimension to the API — e.g. an AI-cost budget — only
 * requires appending one descriptor here; the banner picks it up automatically.
 *
 * Each entry names the `UsageResponse` field carrying the used figure and the
 * `UsageLimits` field carrying its cap.
 */
export const NUDGE_METRIC_DESCRIPTORS: ReadonlyArray<{
  key: string
  usageField: keyof UsageResponse
  /** Reads this metric's cap out of the limit set (not all caps are top-level scalars). */
  limitOf: (limits: UsageLimits) => unknown
  storage?: boolean
  cost?: boolean
}> = [
  { key: 'deployments', usageField: 'deployments', limitOf: (l) => l.maxActiveDeployments },
  { key: 'customDomains', usageField: 'customDomains', limitOf: (l) => l.maxCustomDomains },
  { key: 'storageMb', usageField: 'storageMb', limitOf: (l) => l.maxStorageMb, storage: true },
  {
    key: 'aiCost',
    usageField: 'aiCostCentsToday',
    limitOf: (l) => l.aiBudget?.cents,
    cost: true,
  },
]

/** Numeric coercion that floors non-finite / negative values to 0. */
function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}

/**
 * Roll a usage response up to a single proactive-nudge decision.
 *
 * Computes {@link limitState} for EVERY metered dimension described in
 * {@link NUDGE_METRIC_DESCRIPTORS} (iterated generically so new limits are
 * picked up automatically) and reports:
 *
 * - `severity` — the worst state across all *bounded* metrics: `'over'` if any
 *   metric is at/over its cap, else `'approaching'` if any is near it, else
 *   `'none'`. Unlimited metrics never contribute (they can't be approached).
 * - `metrics` — the resolved per-metric list (excluding unlimited dimensions),
 *   so callers can render which limit(s) are tight.
 *
 * Pure: no HTTP, no React. Safe to call with `null` (→ `'none'`, empty list).
 *
 * @param usage - The `/account/usage` (or project usage) response, or null.
 * @param approachingFraction - The fraction at which to warn (defaults to
 *   {@link APPROACHING_FRACTION}).
 * @returns The nudge roll-up.
 */
export function nudgeStateFromUsage(
  usage: UsageResponse | null | undefined,
  approachingFraction: number = APPROACHING_FRACTION,
): NudgeRollup {
  if (!usage || !usage.limits) return { severity: 'none', metrics: [] }

  const metrics: NudgeMetric[] = []
  for (const desc of NUDGE_METRIC_DESCRIPTORS) {
    const limit = toCount(desc.limitOf(usage.limits))
    // Unlimited dimensions can't be "approached" — skip them entirely so they
    // never appear in the banner or influence severity.
    if (isUnlimited(limit)) continue
    const used = toCount(usage[desc.usageField] as unknown)
    const state = limitState(used, limit, approachingFraction)
    metrics.push({ key: desc.key, storage: desc.storage, cost: desc.cost, used, limit, state })
  }

  let severity: NudgeSeverity = 'none'
  if (metrics.some((m) => m.state === 'over')) severity = 'over'
  else if (metrics.some((m) => m.state === 'approaching')) severity = 'approaching'

  return { severity, metrics }
}

/**
 * The bounded metrics whose state is `'approaching'` or `'over'` — the subset
 * the banner actually surfaces, worst-first (`'over'` before `'approaching'`).
 *
 * @param rollup - The roll-up from {@link nudgeStateFromUsage}.
 * @returns The tight metrics, over-state first.
 */
export function tightMetrics(rollup: NudgeRollup): NudgeMetric[] {
  return rollup.metrics
    .filter((m) => m.state === 'approaching' || m.state === 'over')
    .sort((a, b) => (a.state === b.state ? 0 : a.state === 'over' ? -1 : 1))
}

/**
 * Drop one metric from a roll-up and recompute its severity from what remains —
 * for a dimension that is metered but currently cannot stop the user (the AI
 * allowance while funded extra usage is on).
 *
 * @param rollup - The roll-up from {@link nudgeStateFromUsage}.
 * @param key - The metric key to drop (e.g. `'aiCost'`).
 * @returns The roll-up without that metric.
 */
export function withoutNudgeMetric(rollup: NudgeRollup, key: string): NudgeRollup {
  const metrics = rollup.metrics.filter((m) => m.key !== key)
  const severity: NudgeSeverity = metrics.some((m) => m.state === 'over')
    ? 'over'
    : metrics.some((m) => m.state === 'approaching')
      ? 'approaching'
      : 'none'
  return { severity, metrics }
}

/**
 * A stable dismissal signature for a nudge: the billing period plus the sorted
 * set of currently-tight `metric:state` pairs. Persisting this (rather than a
 * bare boolean) means a dismissed banner re-shows when a NEW metric crosses the
 * threshold, when a metric escalates `approaching → over`, or on a new billing
 * period — exactly the cases the user should be re-nudged.
 *
 * @param usage - The usage response (for the period window).
 * @param rollup - The roll-up from {@link nudgeStateFromUsage}.
 * @returns A signature string, or `''` when there is nothing to nudge about.
 */
export function nudgeDismissSignature(
  usage: UsageResponse | null | undefined,
  rollup: NudgeRollup,
): string {
  if (!usage || rollup.severity === 'none') return ''
  const period = usage.period?.start ?? ''
  const tight = tightMetrics(rollup)
    .map((m) => `${m.key}:${m.state}`)
    .sort()
    .join(',')
  return `${period}|${tight}`
}

/**
 * Total brokered requests across every (provider, category) line. Convenience
 * for an account-level "AI/brokered requests this period" summary.
 *
 * @param broker - The broker usage lines.
 * @returns The summed request count.
 */
export function totalBrokerRequests(broker: BrokerUsageLine[]): number {
  return broker.reduce((sum, l) => sum + (Number.isFinite(l.requests) ? l.requests : 0), 0)
}

/**
 * Total brokered units across every (provider, category) line.
 *
 * @param broker - The broker usage lines.
 * @returns The summed unit count.
 */
export function totalBrokerUnits(broker: BrokerUsageLine[]): number {
  return broker.reduce((sum, l) => sum + (Number.isFinite(l.units) ? l.units : 0), 0)
}

/**
 * A human label for a brokered (provider, category) pair, e.g. `"anthropic · ai"`.
 *
 * @param line - The broker usage line.
 * @returns A display label.
 */
export function brokerLabel(line: BrokerUsageLine): string {
  return `${line.provider} · ${line.category}`
}

/**
 * Whether a broker line carries a usable billed/at-cost figure (a finite,
 * non-negative `costCents`). Lines without one are summarized by requests/units.
 *
 * @param line - The broker usage line.
 * @returns True when `costCents` is present and renderable.
 */
export function hasBrokerCost(line: BrokerUsageLine): boolean {
  const cents = brokerCents(line)
  return typeof cents === 'number' && Number.isFinite(cents) && cents >= 0
}

/**
 * The cents figure of a broker line: `billedCents` (what the server sends), else `costCents`.
 *
 * @param line - The broker usage line.
 * @returns The figure, or undefined when the line carries none.
 */
export function brokerCents(line: BrokerUsageLine): number | undefined {
  return line.billedCents ?? line.costCents
}

/**
 * Format a money figure given in cents as a dollar string, e.g. `1234` → `"$12.34"`.
 * Non-finite inputs floor to `"$0.00"`; a negative amount (a balance a refund or
 * chargeback took below zero) renders as `"−$X.XX"`, never as `"$0.00"`.
 *
 * @param cents - The amount in cents.
 * @returns A `$`-prefixed, two-decimal display string.
 */
export function formatCents(cents: number): string {
  if (!Number.isFinite(cents)) return '$0.00'
  const sign = cents < 0 ? '−' : ''
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`
}

/**
 * Total billed cost across every broker line that carries a `costCents` figure
 * (lines without one contribute 0). Convenience for a per-project "service
 * spend this period" total.
 *
 * @param broker - The broker usage lines.
 * @returns The summed billed cents.
 */
export function totalBrokerCostCents(broker: BrokerUsageLine[]): number {
  return broker.reduce((sum, l) => sum + (hasBrokerCost(l) ? (brokerCents(l) as number) : 0), 0)
}

// ---------------------------------------------------------------------------
// Overall per-user AI cost (P2-05) — the durable Synthase IDE-chat spend total
// ---------------------------------------------------------------------------
//
// The account-wide `GET /account/usage` response now carries the user's durable
// "overall AI cost": `aiUsageCentsTotal` (lifetime) + `aiUsageCentsThisPeriod`
// (month-to-date), summed from the server-side `aiUsage` ledger. These helpers
// shape those figures for display (render with {@link formatCents}); they are
// the IDE's own platform-paid spend and stay SEPARATE from the brokered-app
// `broker[].costCents` lines — never summed into one conflated number.

/**
 * Whether the usage response carries the durable per-user "overall AI cost"
 * figure ({@link UsageResponse.aiUsageCentsTotal}). Only `GET /account/usage`
 * supplies it (project usage + older API builds omit it), so the account-wide
 * dashboard should render the overall-AI-spend row only when this is true,
 * rather than show a misleading `$0.00` for an endpoint that never reports it.
 *
 * @param usage - The usage response, or null.
 * @returns True when the API supplied a finite, non-negative lifetime AI cost.
 */
export function hasOverallAiCost(usage: UsageResponse | null | undefined): boolean {
  const total = usage?.aiUsageCentsTotal
  return typeof total === 'number' && Number.isFinite(total) && total >= 0
}

/**
 * The user's durable LIFETIME "overall AI cost" in cents (the Synthase IDE-chat
 * spend total), coerced to a non-negative finite number. Render with
 * {@link formatCents}. Returns 0 when the field is absent (project usage / older
 * API) — pair with {@link hasOverallAiCost} when "absent" and "zero" must look
 * different.
 *
 * @param usage - The usage response, or null.
 * @returns The lifetime overall AI cost in cents (0 when absent).
 */
export function overallAiCostCents(usage: UsageResponse | null | undefined): number {
  return toCount(usage?.aiUsageCentsTotal)
}

/**
 * The durable Synthase IDE-chat AI spend for the CURRENT billing period
 * (month-to-date) in cents, coerced to a non-negative finite number — the
 * period-scoped counterpart to {@link overallAiCostCents}, matching the window
 * the brokered `period` figures cover. Render with {@link formatCents}.
 *
 * @param usage - The usage response, or null.
 * @returns This period's IDE-chat AI cost in cents (0 when absent).
 */
export function periodAiCostCents(usage: UsageResponse | null | undefined): number {
  return toCount(usage?.aiUsageCentsThisPeriod)
}

// ---------------------------------------------------------------------------
// Per-project usage inspector — shaping a UsageResponse into render-ready rows
// ---------------------------------------------------------------------------

/** A render-ready brokered-service usage row for the per-project inspector. */
export interface ProjectUsageRow {
  /** Registry provider key, e.g. `anthropic`. */
  provider: string
  /** Brokerability class, e.g. `ai`. */
  category: string
  /** `"provider · category"` display label. */
  label: string
  /** Total forwarded requests in the period. */
  requests: number
  /** Total normalized units (tokens for AI). */
  units: number
  /** Billed cost in cents, when the line carried one (else null). */
  costCents: number | null
}

/** The shaped per-project usage inspector view model. */
export interface ProjectUsageView {
  /** The billing period window (null when the response omits it). */
  period: BillingPeriod | null
  /** Brokered-service rows, requests-descending (then units-descending). */
  rows: ProjectUsageRow[]
  /** True when there is no brokered usage to show this period. */
  empty: boolean
  /** Summed requests across all rows. */
  totalRequests: number
  /** Summed units across all rows. */
  totalUnits: number
  /** Summed billed cents across rows that carried a cost (null when none did). */
  totalCostCents: number | null
  /** AI spend so far in the current allowance window, in cents. */
  aiCostCentsToday: number
  /** The window's AI spend budget in cents (the binding cap). */
  aiCostCentsLimit: number
  /** Limit state of the window's AI spend vs its budget. */
  aiCostState: LimitState
  /**
   * The user-facing meter: 0–100% of the current window's AI allowance used.
   * Prefers the server's `aiMeter.usedPercent` (the authoritative figure); falls
   * back to the cents-derived ratio for older API responses. AI usage is
   * presented as this UNITLESS percentage everywhere — never as a currency
   * amount.
   */
  aiUsedPercent: number
  /** ISO end of the current allowance window from the server meter, if known. */
  aiResetsAt: string | null
  /**
   * WHICH window that is — `'daily'` on the unpaid tiers, `'billing-period'`
   * (monthly) on the paid ones. Carried so the inspector can label the meter
   * without assuming a day; `undefined` on an API that predates the meter.
   */
  aiWindow: 'daily' | 'billing-period' | undefined
}

/**
 * Shape a per-project `GET /projects/:id/usage` response into the render-ready
 * view model the {@link UsageInspector} consumes. Pure: no HTTP, no React.
 *
 * - Coerces every numeric figure to a non-negative finite value.
 * - Sorts broker rows requests-descending, then units-descending, so the
 *   busiest service is first.
 * - Surfaces a per-line billed cost only when the line actually carried one;
 *   the view's `totalCostCents` is null unless at least one row did (so the UI
 *   can fall back to requests/units presentation when no cost is reported).
 * - Derives today's AI-spend {@link LimitState} against the daily budget.
 *
 * Safe to call with `null` / `undefined` (→ empty view, `aiCostState: 'ok'`).
 *
 * @param usage - The per-project usage response, or null.
 * @returns The shaped per-project usage view model.
 */
export function projectUsageView(usage: UsageResponse | null | undefined): ProjectUsageView {
  const broker = Array.isArray(usage?.broker) ? usage.broker : []

  const rows: ProjectUsageRow[] = broker.map((line) => ({
    provider: line.provider,
    category: line.category,
    label: brokerLabel(line),
    requests: toCount(line.requests),
    units: toCount(line.units),
    costCents: hasBrokerCost(line) ? toCount(brokerCents(line)) : null,
  }))

  rows.sort((a, b) => b.requests - a.requests || b.units - a.units)

  const anyCost = rows.some((r) => r.costCents != null)
  const totalCostCents = anyCost ? rows.reduce((sum, r) => sum + (r.costCents ?? 0), 0) : null

  const aiCostCentsToday = toCount(usage?.aiCostCentsToday)
  const aiCostCentsLimit = toCount(usage?.limits?.aiBudget?.cents)
  const meter = usage?.aiMeter
  const aiUsedPercent =
    typeof meter?.usedPercent === 'number'
      ? Math.max(0, Math.min(100, Math.floor(meter.usedPercent)))
      : aiCostCentsLimit > 0
        ? aiCostCentsToday >= aiCostCentsLimit
          ? 100
          : Math.min(99, Math.floor((aiCostCentsToday / aiCostCentsLimit) * 100))
        : 0

  return {
    period: usage?.period ?? null,
    rows,
    empty: rows.length === 0,
    totalRequests: rows.reduce((sum, r) => sum + r.requests, 0),
    totalUnits: rows.reduce((sum, r) => sum + r.units, 0),
    totalCostCents,
    aiCostCentsToday,
    aiCostCentsLimit,
    aiCostState: limitState(aiCostCentsToday, aiCostCentsLimit),
    aiUsedPercent,
    aiResetsAt: typeof meter?.resetsAt === 'string' ? meter.resetsAt : null,
    aiWindow:
      meter?.window === 'billing-period' || meter?.window === 'daily' ? meter.window : undefined,
  }
}

// ---------------------------------------------------------------------------
// Daily AI-budget reset tracking (mvp CST4)
// ---------------------------------------------------------------------------
//
// The free daily AI budget refreshes every UTC midnight. To show a returning
// user a positive "your budget reset" banner, the app records the UTC day on
// which the cap was last HIT (set from the limit-error interceptor) and, on a
// later session, checks whether that day is now in the past — i.e. the budget
// has since refreshed. All pure so it stays unit-testable; the component is a
// thin shell.

/** Milliseconds in one UTC day. */
export const UTC_DAY_MS = 86_400_000

/**
 * Storage key (via the `@molecule/app-storage` adapter, NOT raw localStorage)
 * under which the UTC day-bucket of the last daily AI-limit hit is recorded.
 * Written by the HTTP limit-error interceptor on an `ai_cost` / `ai_messages`
 * 429; read by the returning-session budget-reset banner.
 */
export const AI_BUDGET_HIT_DAY_KEY = 'mol_ai_budget_hit_day'

/**
 * The UTC day-bucket for an epoch-ms timestamp — `floor(ms / UTC_DAY_MS)`. This
 * is the SAME bucket the API uses to window the daily AI budget
 * (`ai-cost.ts` `dayBucket`), so a recorded hit-day compares apples-to-apples
 * with the server's reset boundary.
 *
 * @param ms - Epoch milliseconds.
 * @returns The integer UTC day-bucket (0 for non-finite / negative input).
 */
export function utcDayBucket(ms: number): number {
  if (!Number.isFinite(ms) || ms < 0) return 0
  return Math.floor(ms / UTC_DAY_MS)
}

/**
 * Epoch-ms of the next daily-limit reset — the next UTC midnight. Frontend
 * mirror of the API's `nextUtcDailyReset` (`tiers.ts`), for surfaces that know a
 * cap is daily but aren't handed a server-computed `resetAt` (e.g. the proactive
 * nudge reading `/account/usage`).
 *
 * @param now - Reference time in epoch ms (defaults to `Date.now()`).
 * @returns The next UTC-midnight boundary in epoch ms.
 */
export function nextUtcDailyResetMs(now: number = Date.now()): number {
  return (utcDayBucket(now) + 1) * UTC_DAY_MS
}

/**
 * Whether the daily AI budget has reset since it was last hit. True only when a
 * hit-day was recorded AND it is strictly before today's UTC day-bucket — i.e.
 * at least one daily reset has elapsed since the user was capped, so they now
 * have a fresh budget. A hit recorded for the current day (still capped, or
 * just reset within the same UTC day) returns false; a missing/invalid marker
 * returns false.
 *
 * @param hitDayBucket - The recorded UTC day-bucket of the last cap hit, or null.
 * @param now - Reference time in epoch ms (defaults to `Date.now()`).
 * @returns True when the budget has refreshed since the recorded hit.
 */
export function budgetHasResetSince(
  hitDayBucket: number | null | undefined,
  now: number = Date.now(),
): boolean {
  if (hitDayBucket == null || !Number.isFinite(hitDayBucket) || hitDayBucket < 0) return false
  return hitDayBucket < utcDayBucket(now)
}

/**
 * Listeners notified when account usage becomes stale (a budget/limit response).
 * Decoupled pub/sub so the HTTP error interceptor (the producer) and a mounted
 * UsageNudge (the consumer) need not know about each other.
 */
const usageInvalidationListeners = new Set<() => void>()

/**
 * Subscribe to usage-invalidation events so a mounted nudge can re-fetch
 * `/account/usage` the moment a budget/limit response arrives — instead of the
 * banner only appearing after a full page refresh.
 *
 * @param listener - Called when usage data should be re-fetched.
 * @returns An unsubscribe function.
 */
export function onUsageInvalidated(listener: () => void): () => void {
  usageInvalidationListeners.add(listener)
  return () => {
    usageInvalidationListeners.delete(listener)
  }
}

/**
 * Notify all subscribers that usage data is stale and should be re-fetched. A
 * listener that throws is isolated so it can't break the producer or its peers.
 */
export function notifyUsageInvalidated(): void {
  for (const listener of usageInvalidationListeners) {
    try {
      listener()
    } catch (_error) {
      // A misbehaving subscriber must not break the producer or other listeners.
    }
  }
}
